/**
 * Working time one member schedule offers on one local date, in 5-minute
 * slots — the booking engine's availability rules without appointments.
 *
 * Mirrors `apps/api/src/modules/appointments/openings.ts`
 * (`getScheduleOpeningsRangeBatch` + `getOpeningsCore`):
 *   blocking time off      → nothing
 *   ExtraHour `hours` set  → that date's override day, else the weekly day
 *   closed / no slots      → nothing (extra slots are NOT added on such a day)
 *   otherwise              → hours slots − Break slots − weekly breaks
 *                            ∪ ExtraSlot times
 * Weekly breaks (`weeklyBreaks.ts`) always come from the WEEKLY day entry,
 * even when an ExtraHour overrides that date's hours.
 * A slot only counts when its "HH:mm" string is canonical, because the engine
 * checks availability with canonical strings.
 *
 * Used by the daily availability snapshot (API) and Insights utilization
 * estimates (dashboard). `apps/api/src/modules/schedules/__tests__/
 * memberDayAvailabilityParity.test.ts` drives the real engine against this
 * module — change both together.
 */
import {
  MIN_TIME_BLOCK_MINUTES,
  expandTimeRangeToSlots,
  minutesToDbTime,
  parseDbTimeToMinutes,
} from "./time";
import {
  normalizeWeeklyBreaks,
  weeklyBreakBlockedSlots,
  type WeeklyBreak,
} from "./weeklyBreaks";

/**
 * Bump when the computation changes; stored on every snapshot row.
 * 2 (Oct 2026): weekly breaks on the day entry count as blocked time.
 */
export const MEMBER_DAY_AVAILABILITY_VERSION = 2;

export interface WeeklyDayHours {
  day: number;
  open: string | null;
  close: string | null;
  isOpen: boolean;
  /** Weekly breaks; present only on an open day that has some. */
  breaks?: WeeklyBreak[];
}

/** `entry` with its weekly breaks, or without the key when there are none. */
const withBreaks = <T extends WeeklyDayHours>(entry: T, raw: unknown): T => {
  if (!entry.isOpen) return entry;
  const breaks = normalizeWeeklyBreaks(raw);
  return breaks.length ? { ...entry, breaks } : entry;
};

// ---------------------------------------------------------------------------
// Weekly hours normalization — copied verbatim from the booking engine
// (`deriveBusinessHours` and helpers). Keep in sync.
// ---------------------------------------------------------------------------

const DEFAULT_DAY_TEMPLATE: WeeklyDayHours[] = Array.from({ length: 7 }).map(
  (_, index) => ({
    day: index,
    open: null,
    close: null,
    isOpen: false,
  }),
);

const cloneDefaultWeek = (): WeeklyDayHours[] =>
  DEFAULT_DAY_TEMPLATE.map((entry) => ({ ...entry }));

const coerceTime = (value: unknown): string | null => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return minutesToDbTime(value);
  }
  return null;
};

const coerceIsOpen = (value: unknown, defaultValue: boolean): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "off" || normalized === "closed") return false;
    if (normalized === "on" || normalized === "open") return true;
  }
  if (typeof value === "number") {
    return value !== 0;
  }
  if (value == null) return defaultValue;
  return Boolean(value);
};

const normalizeDayEntry = (index: number, value: any): WeeklyDayHours => {
  if (!value) {
    return { ...DEFAULT_DAY_TEMPLATE[index] };
  }

  if (Array.isArray(value)) {
    const open = value[0]?.trim?.() ?? value[0] ?? null;
    const close = value[1]?.trim?.() ?? value[1] ?? null;
    const hasTimes = Boolean(open && close);
    const status = value[2];
    const isOpen = status == null ? hasTimes : coerceIsOpen(status, hasTimes);
    return {
      day: index,
      open: hasTimes ? open : null,
      close: hasTimes ? close : null,
      isOpen: Boolean(isOpen && hasTimes),
    };
  }

  if (typeof value === "object") {
    const open = coerceTime(
      value.open ?? value.o ?? value.start ?? value.from,
    );
    const close = coerceTime(value.close ?? value.c ?? value.end ?? value.to);
    const hasTimes = Boolean(open && close);
    const isOpen = coerceIsOpen(
      value.isOpen ?? value.on ?? value.status ?? value.openStatus,
      hasTimes,
    );

    const declaredDay = value.day;
    const normalizedDay =
      typeof declaredDay === "number" && Number.isInteger(declaredDay)
        ? Math.max(0, Math.min(6, declaredDay))
        : index;

    return withBreaks(
      {
        day: normalizedDay,
        open: hasTimes ? open : null,
        close: hasTimes ? close : null,
        isOpen: Boolean(isOpen && hasTimes),
      },
      value.breaks,
    );
  }

  return { ...DEFAULT_DAY_TEMPLATE[index] };
};

const ensureDayEntry = (
  entry: WeeklyDayHours | undefined,
  fallbackDay: number,
): WeeklyDayHours => {
  if (!entry) {
    return { ...DEFAULT_DAY_TEMPLATE[fallbackDay] };
  }
  const open = typeof entry.open === "string" ? entry.open : null;
  const close = typeof entry.close === "string" ? entry.close : null;
  const hasTimes = Boolean(open && close);
  return withBreaks(
    {
      day: fallbackDay,
      open: hasTimes ? open : null,
      close: hasTimes ? close : null,
      isOpen: Boolean(entry.isOpen && hasTimes),
    },
    entry.breaks,
  );
};

function hoursMapToWeeklyHours(hoursMap: any): WeeklyDayHours[] {
  if (!hoursMap) return cloneDefaultWeek();
  const arr = cloneDefaultWeek();
  for (let i = 0; i < 7; i++) {
    const entry = hoursMap[i] ?? hoursMap[String(i)];
    if (!entry) {
      arr[i] = { ...DEFAULT_DAY_TEMPLATE[i] };
      continue;
    }
    arr[i] = ensureDayEntry(normalizeDayEntry(i, entry), i);
  }
  return arr;
}

function hoursArrayToWeeklyHours(hoursArray: any[]): WeeklyDayHours[] {
  if (!Array.isArray(hoursArray)) return cloneDefaultWeek();
  return cloneDefaultWeek().map((_, index) =>
    normalizeDayEntry(
      index,
      hoursArray.find((entry) => entry?.day === index) ?? hoursArray[index],
    ),
  );
}

/** The engine's `deriveBusinessHours`: any stored hours JSON → 7 day entries. */
export function normalizeWeeklyHours(raw: unknown): WeeklyDayHours[] {
  if (Array.isArray(raw)) {
    return hoursArrayToWeeklyHours(raw);
  }
  if (typeof raw === "object" && raw !== null) {
    return hoursMapToWeeklyHours(raw);
  }
  return cloneDefaultWeek();
}

/** The engine's `extractHourFromData`: the hours that apply on `weekday`. */
export function resolveDayHours(
  weekly: WeeklyDayHours[],
  overrideHours: unknown,
  weekday: number,
): WeeklyDayHours {
  const baseEntry = ensureDayEntry(
    weekly.find((entry) => entry.day === weekday) ?? weekly[weekday],
    weekday,
  );
  if (!overrideHours) return baseEntry;
  const overrides = normalizeWeeklyHours(overrideHours);
  return ensureDayEntry(
    overrides.find((entry) => entry.day === weekday) ?? overrides[weekday],
    weekday,
  );
}

// ---------------------------------------------------------------------------
// Breaks
// ---------------------------------------------------------------------------

export interface BreakRecord {
  time: string | null;
  start: string | null;
  end: string | null;
}

/**
 * Parses a time the way `moment(value, "HH:mm")` does in non-strict mode for
 * the shapes stored in Break rows ("HH:mm", "H:mm", "HH:mm:ss", "0900"):
 * minutes after midnight, or null when moment would call it invalid.
 * "24:00" is the NEXT midnight (1440): moment rolls it to the following day,
 * so a break "23:00"–"24:00" covers the whole hour. Slot strings still wrap
 * ("24:00" as a start emits "00:00", like moment's format).
 */
export function parseLooseTimeToMinutes(value: string): number | null {
  const match = /^\s*(\d{1,2})(?::?(\d{1,2}))?/.exec(value);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = match[2] === undefined ? 0 : Number(match[2]);
  if (minutes > 59) return null;
  if (hours === 24 && minutes === 0) return 24 * 60;
  if (hours > 23) return null;
  return hours * 60 + minutes;
}

/** The engine's `expandBreakRecord`: the slot strings a Break row removes. */
export function expandBreakSlots(record: BreakRecord): string[] {
  const slots = new Set<string>();
  const primary = record.time?.trim();
  if (primary) slots.add(primary);

  const start = (record.start ?? primary)?.trim();
  const end = record.end?.trim();
  if (!start) return Array.from(slots);

  const startMinutes = parseLooseTimeToMinutes(start);
  if (startMinutes == null) return Array.from(slots);

  const endMinutes = end ? parseLooseTimeToMinutes(end) : null;
  if (endMinutes == null || endMinutes <= startMinutes) {
    slots.add(minutesToDbTime(startMinutes));
    return Array.from(slots);
  }

  const blocks = Math.max(
    1,
    Math.ceil((endMinutes - startMinutes) / MIN_TIME_BLOCK_MINUTES),
  );
  for (let i = 0; i < blocks; i++) {
    slots.add(minutesToDbTime(startMinutes + i * MIN_TIME_BLOCK_MINUTES));
  }
  return Array.from(slots);
}

// ---------------------------------------------------------------------------
// One member schedule, one date
// ---------------------------------------------------------------------------

export interface MemberDayAvailabilityInput {
  /** Location-local date, YYYY-MM-DD. */
  date: string;
  /** The member schedule's `Schedule.hours` JSON. */
  weeklyHours: unknown;
  /** `ExtraHour.hours` for this schedule and date; null/undefined when none. */
  overrideHours?: unknown;
  /** Break rows for this schedule and date. */
  breaks: readonly BreakRecord[];
  /** `ExtraSlot.time` values for this schedule and date. */
  extraSlotTimes: readonly (string | null)[];
  /** A blocking (PENDING/APPROVED) time off covers this date. */
  timeOff: boolean;
}

export type AvailabilityHoursSource = "weekly" | "override" | "time_off";

export interface MemberDayAvailability {
  hoursSource: AvailabilityHoursSource;
  /** Minutes in the day's hours before breaks. */
  scheduledMinutes: number;
  /** Scheduled minutes removed by breaks. */
  blockedMinutes: number;
  /** Extra-slot minutes outside the remaining scheduled time. */
  extraMinutes: number;
  /** Minutes the available slots cover (overlapping off-grid slots once). */
  availableMinutes: number;
  /** Sorted slot starts, minutes after local midnight (engine parity). */
  slots: number[];
  /**
   * Covered minutes as contiguous runs, "HH:mm-HH:mm" with an exclusive end
   * ("24:00" allowed). Minute counts and ranges never exceed one day.
   */
  ranges: string[];
}

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

function weekdayOf(date: string): number {
  if (!DATE_REGEX.test(date)) {
    throw new Error(`Expected a YYYY-MM-DD date, got "${date}"`);
  }
  const weekday = new Date(`${date}T00:00:00.000Z`).getUTCDay();
  if (Number.isNaN(weekday)) {
    throw new Error(`Expected a YYYY-MM-DD date, got "${date}"`);
  }
  return weekday;
}

/** Minutes of a canonical "HH:mm" slot string, else null. */
function canonicalSlotMinutes(slot: string): number | null {
  const minutes = parseDbTimeToMinutes(slot);
  if (minutes == null || minutes >= 24 * 60) return null;
  return minutesToDbTime(minutes) === slot ? minutes : null;
}

function emptyDay(hoursSource: AvailabilityHoursSource): MemberDayAvailability {
  return {
    hoursSource,
    scheduledMinutes: 0,
    blockedMinutes: 0,
    extraMinutes: 0,
    availableMinutes: 0,
    slots: [],
    ranges: [],
  };
}

export function computeMemberDayAvailability(
  input: MemberDayAvailabilityInput,
): MemberDayAvailability {
  const weekday = weekdayOf(input.date);
  if (input.timeOff) return emptyDay("time_off");

  const hoursSource: AvailabilityHoursSource = input.overrideHours
    ? "override"
    : "weekly";
  const weekly = normalizeWeeklyHours(input.weeklyHours);
  const day = resolveDayHours(weekly, input.overrideHours, weekday);
  if (!day.isOpen || !day.open || !day.close) return emptyDay(hoursSource);

  const timeSlots = expandTimeRangeToSlots(day.open, day.close);
  if (timeSlots.length === 0) return emptyDay(hoursSource);

  const available = new Set(timeSlots);
  for (const record of input.breaks) {
    for (const slot of expandBreakSlots(record)) available.delete(slot);
  }
  // Weekly breaks come from the weekly entry even on an override date.
  const weeklyEntry =
    weekly.find((entry) => entry.day === weekday) ?? weekly[weekday];
  for (const slot of weeklyBreakBlockedSlots(weeklyEntry?.breaks, timeSlots)) {
    available.delete(slot);
  }
  const afterBreaks = new Set(available);
  for (const time of input.extraSlotTimes) {
    if (time) available.add(time);
  }

  const toMinutes = (values: Iterable<string>): Set<number> => {
    const out = new Set<number>();
    for (const value of values) {
      const minutes = canonicalSlotMinutes(value);
      if (minutes != null) out.add(minutes);
    }
    return out;
  };

  const slots = [...toMinutes(available)].sort((a, b) => a - b);
  const scheduled = slotCoverage(toMinutes(timeSlots));
  const remaining = slotCoverage(toMinutes(afterBreaks));
  const covered = slotCoverage(slots);

  return {
    hoursSource,
    scheduledMinutes: countMinutes(scheduled),
    blockedMinutes: countMinutes(scheduled, remaining),
    extraMinutes: countMinutes(covered, remaining),
    availableMinutes: countMinutes(covered),
    slots,
    ranges: coverageToRanges(covered),
  };
}

// ---------------------------------------------------------------------------
// Minute coverage and range encoding (snapshot storage)
// ---------------------------------------------------------------------------

const MINUTES_PER_DAY = 24 * 60;

/** Minute-of-day mask covered by 5-minute slots starting at `starts`. */
function slotCoverage(starts: Iterable<number>): Uint8Array {
  const mask = new Uint8Array(MINUTES_PER_DAY);
  for (const start of starts) {
    const end = Math.min(start + MIN_TIME_BLOCK_MINUTES, MINUTES_PER_DAY);
    for (let m = Math.max(0, start); m < end; m += 1) mask[m] = 1;
  }
  return mask;
}

/** Minutes set in `mask` (and not in `minus`, when given). */
function countMinutes(mask: Uint8Array, minus?: Uint8Array): number {
  let count = 0;
  for (let m = 0; m < MINUTES_PER_DAY; m += 1) {
    if (mask[m] && !(minus && minus[m])) count += 1;
  }
  return count;
}

function formatRangeBoundary(minutes: number): string {
  return minutes === MINUTES_PER_DAY ? "24:00" : minutesToDbTime(minutes);
}

function coverageToRanges(mask: Uint8Array): string[] {
  const ranges: string[] = [];
  let runStart: number | null = null;
  for (let m = 0; m <= MINUTES_PER_DAY; m += 1) {
    const on = m < MINUTES_PER_DAY && mask[m] === 1;
    if (on && runStart === null) runStart = m;
    if (!on && runStart !== null) {
      ranges.push(`${formatRangeBoundary(runStart)}-${formatRangeBoundary(m)}`);
      runStart = null;
    }
  }
  return ranges;
}

/** 5-minute slot starts → the contiguous runs of minutes they cover. */
export function slotsToRanges(slots: Iterable<number>): string[] {
  return coverageToRanges(slotCoverage(slots));
}

/** Sorted minutes of day covered by "HH:mm-HH:mm" runs. Malformed entries are skipped. */
export function rangesToMinutes(ranges: readonly string[]): number[] {
  const mask = new Uint8Array(MINUTES_PER_DAY);
  for (const range of ranges) {
    const [from, to] = range.split("-");
    const start = parseDbTimeToMinutes(from);
    const end = parseDbTimeToMinutes(to);
    if (start == null || end == null || end <= start) continue;
    for (let m = start; m < Math.min(end, MINUTES_PER_DAY); m += 1) mask[m] = 1;
  }
  const minutes: number[] = [];
  for (let m = 0; m < MINUTES_PER_DAY; m += 1) if (mask[m]) minutes.push(m);
  return minutes;
}
